import React, { useState, useEffect, useCallback } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { getPendingUploads, removePendingUpload } from '../lib/offlineQueue';
import { uploadToWasabi } from '../lib/wasabi';

const OfflineQueueStatus = () => {
  const [pending, setPending]   = useState([]);
  const [retrying, setRetrying] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  const refresh = useCallback(async () => {
    const items = await getPendingUploads();
    setPending(items);
  }, []);

  useEffect(() => {
    refresh();
    const onOnline  = () => { setIsOnline(true); refresh(); };
    const onOffline = () => setIsOnline(false);
    window.addEventListener('online',  onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online',  onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, [refresh]);

  const retryAll = async () => {
    if (!isOnline || retrying) return;
    setRetrying(true);
    const items = await getPendingUploads();
    for (const item of items) {
      try {
        // Pass userId and originalAwb stored at save-time so CSV is written
        await uploadToWasabi(
          item.blob,
          item.filename,
          () => {},
          item.userId      || null,
          item.originalAwb || item.filename.replace(/\.webm$/i, ''),
        );
        await removePendingUpload(item.id);
      } catch {
        // keep in queue — will retry next time
      }
    }
    await refresh();
    setRetrying(false);
  };

  if (pending.length === 0) return null;

  return (
    <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mb-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <SafeIcon icon={FiIcons.FiWifi} className="text-amber-500" />
          <div>
            <p className="text-sm font-bold text-amber-800">
              {pending.length} upload{pending.length > 1 ? 's' : ''} pending
            </p>
            <p className="text-xs text-amber-600">
              {isOnline
                ? 'Connection restored — ready to retry'
                : 'Offline — will retry when connected'}
            </p>
          </div>
        </div>
        {isOnline && (
          <button
            onClick={retryAll}
            disabled={retrying}
            className="text-xs bg-amber-500 hover:bg-amber-600 text-white px-3 py-1.5 rounded-lg font-bold flex items-center gap-1 disabled:opacity-50"
          >
            {retrying
              ? <div className="w-3 h-3 border border-white/30 border-t-white rounded-full animate-spin" />
              : <SafeIcon icon={FiIcons.FiRefreshCw} />}
            Retry
          </button>
        )}
      </div>
    </div>
  );
};

export default OfflineQueueStatus;